/**
 * ATRIBUCIÓN DE LEADS — de qué anuncio salió cada cliente.
 *
 * El problema que resuelve: el píxel disparaba `PageView` y nada más. Meta no puede optimizar
 * por compradores si nunca le decís quiénes son —te optimiza por visitas a la landing— y nosotros
 * no podíamos calcular el CAC porque el hilo entre "vio el reel" y "paga todos los meses" se
 * cortaba en la landing: el formulario iba a Formspree con un `origen` hardcodeado y el botón de
 * WhatsApp (por donde entra la mayoría) no dejaba ningún rastro.
 *
 * Qué guarda:
 *  - PRIMER toque: la campaña que CREÓ la demanda. No se pisa nunca.
 *  - ÚLTIMO toque: la que cerró. Se pisa en cada visita con parámetros nuevos.
 *  - `fbclid` → `fbc`: el identificador de clic de Meta. Se PERSISTE porque desaparece del
 *    URL apenas navegan, y sin él la conversión offline (cuando el lead termina pagando) no se
 *    puede matchear contra el anuncio. Es el dato más valioso de todo este archivo.
 *
 * IDENTIDAD DE LA VISITA — tres cosas distintas, y la diferencia importa:
 *  - `visitId`  : aleatorio largo (122 bits). Es la CLAVE del lead. No se puede adivinar, así que
 *                 nadie de afuera puede pisar el lead de otro ni enumerar la colección entera.
 *  - `ref`      : 5 caracteres legibles. Va en el mensaje de WhatsApp para poder dictarlo por
 *                 teléfono y buscarlo en el panel. Es SOLO una etiqueta de búsqueda: si dos
 *                 visitas sacan el mismo código, el panel muestra las dos y no se pierde nada.
 *  - `eventId`  : uno por (visita, canal). El MISMO en el píxel del navegador y en el evento del
 *                 servidor → Meta deduplica. Y como se reusa entre clics, tocar los tres botones
 *                 de WhatsApp de la landing sigue siendo UNA conversión, no tres.
 *
 * Los tres viven en `sessionStorage`: duran la visita (que es la unidad que queremos medir) y
 * sobreviven a recargar la página — con `useState` se regeneraban en cada montaje y el mismo
 * visitante contaba como varios leads.
 *
 * Todo va en try/catch: en una landing, un error de storage (modo incógnito, cookies bloqueadas)
 * no puede tirar abajo el formulario. Sin tracking se vende igual; sin formulario no.
 */

const LS_FIRST = "ra_attr_first";
const LS_LAST = "ra_attr_last";
const LS_FBC = "ra_fbc";
const SS_VISIT = "ra_visit";
const SS_REF = "ra_ref";
const SS_EVENT = "ra_ev_";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const CLICK_IDS = ["fbclid", "gclid", "ttclid", "msclkid"];

/* Alfabeto sin 0/O/1/I/L: el código se dicta por WhatsApp y por teléfono. */
const ALFABETO = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

function lsGet(k) { try { return window.localStorage.getItem(k); } catch (_) { return null; } }
function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (_) { /* incógnito */ } }
/* Respaldo en memoria: si sessionStorage tira (Safari privado, cookies bloqueadas), sin esto la
 * identidad de la visita se regeneraba en CADA llamada y volvía la inflación de conversiones que
 * justamente vinimos a arreglar. En memoria dura lo que dura la página, que ya alcanza. */
const memoria = {};
function ssGet(k) {
  // Solo se cae a memoria si sessionStorage TIRA. Si anda y está vacío, está vacío de verdad
  // (si no, una visita nueva heredaría la identidad de la anterior en la misma página).
  try { return window.sessionStorage.getItem(k); } catch (_) { return k in memoria ? memoria[k] : null; }
}
function ssSet(k, v) {
  memoria[k] = v;
  try { window.sessionStorage.setItem(k, v); } catch (_) { /* incógnito */ }
}
function safeJson(raw) { try { return raw ? JSON.parse(raw) : null; } catch (_) { return null; } }

/** Bytes aleatorios de verdad. `Math.random()` no sirve para claves ni para códigos. */
function azar(n) {
  try {
    const a = new Uint8Array(n);
    window.crypto.getRandomValues(a);
    return a;
  } catch (_) {
    const a = new Uint8Array(n);
    for (let i = 0; i < n; i++) a[i] = Math.floor(Math.random() * 256);
    return a;
  }
}

/** Lee una cookie por nombre. El `_fbp` lo escribe el propio píxel al cargar. */
export function cookie(nombre) {
  try {
    const m = document.cookie.match(new RegExp("(^|;\\s*)" + nombre + "=([^;]*)"));
    return m ? decodeURIComponent(m[2]) : null;
  } catch (_) {
    return null;
  }
}

/** Id de ESTA visita. Es la clave del lead: largo y aleatorio para que nadie lo adivine. */
export function visitId() {
  let v = ssGet(SS_VISIT);
  if (v) return v;
  v = Array.from(azar(16), (b) => b.toString(16).padStart(2, "0")).join("");
  ssSet(SS_VISIT, v);
  return v;
}

/** Código corto y legible de esta visita. Etiqueta de búsqueda, NO una clave. */
export function refCode() {
  let r = ssGet(SS_REF);
  if (r) return r;
  const bytes = azar(5);
  r = Array.from(bytes, (b) => ALFABETO[b % ALFABETO.length]).join("");
  ssSet(SS_REF, r);
  return r;
}

/**
 * Id del evento para este canal, estable durante toda la visita. Que sea el mismo en el navegador
 * y en el servidor es lo que hace que Meta no cuente la conversión dos veces; que se reuse entre
 * clics es lo que hace que los tres botones de WhatsApp de la landing no cuenten como tres.
 */
export function eventIdPara(canal) {
  const k = SS_EVENT + canal;
  let e = ssGet(k);
  if (e) return e;
  e = `${canal}-${visitId().slice(0, 16)}`;
  ssSet(k, e);
  return e;
}

/**
 * Lee los parámetros de la URL actual. Devuelve null si no hay NINGUNO: una visita directa no
 * debe pisar el último toque bueno (si no, el que vuelve tipeando el dominio se atribuye a sí
 * mismo y el anuncio que lo trajo pierde el crédito).
 */
function tocarDesdeUrl(now) {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (_) {
    return null;
  }
  const t = { ts: now };
  let hay = false;
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) { t[k] = String(v).slice(0, 120); hay = true; }
  }
  for (const k of CLICK_IDS) {
    const v = params.get(k);
    if (v) { t[k] = String(v).slice(0, 400); hay = true; }
  }
  if (!hay) return null;
  try {
    if (document.referrer) t.referrer = String(document.referrer).slice(0, 300);
  } catch (_) { /* sin referrer */ }
  t.landing = String(window.location.pathname || "/").slice(0, 200);
  return t;
}

/**
 * Corre una vez al montar la landing. Persiste primer/último toque y arma el `fbc` a partir del
 * `fbclid`. Idempotente: llamarla dos veces no rompe nada.
 */
export function capturarAtribucion(now = Date.now()) {
  if (typeof window === "undefined") return;
  const toque = tocarDesdeUrl(now);
  if (!toque) return;

  if (!lsGet(LS_FIRST)) lsSet(LS_FIRST, JSON.stringify(toque));
  lsSet(LS_LAST, JSON.stringify(toque));

  // fbc: el formato que espera Meta es fb.1.<ms>.<fbclid>. Solo se re-escribe si viene un fbclid
  // NUEVO — un clic más reciente gana, pero una visita sin fbclid no puede borrar el anterior.
  if (toque.fbclid) {
    const previo = lsGet(LS_FBC);
    if (!previo || previo.split(".").slice(3).join(".") !== toque.fbclid) {
      lsSet(LS_FBC, `fb.1.${now}.${toque.fbclid}`);
    }
  }
}

/** Todo lo que sabemos del origen de esta persona, listo para mandar al backend. */
export function payloadAtribucion() {
  if (typeof window === "undefined") return {};
  const first = safeJson(lsGet(LS_FIRST));
  const last = safeJson(lsGet(LS_LAST));
  return {
    first: first || null,
    last: last || null,
    fbc: lsGet(LS_FBC) || null,
    fbp: cookie("_fbp") || null,
    url: String(window.location.href || "").slice(0, 500),
    ua: String(window.navigator.userAgent || "").slice(0, 400),
    lang: window.navigator.language || null,
  };
}

/**
 * Dispara el evento en el píxel del navegador. El `eventID` es lo que permite que el mismo evento
 * llegue también por el servidor (Conversions API) sin contarse dos veces: Meta los deduplica por
 * (event_name, event_id). Sin esto, mandar los dos lados inflaría las conversiones al doble.
 */
export function pixel(evento, params, eventId) {
  try {
    if (typeof window.fbq !== "function") return; // bloqueador de anuncios, o la POS local
    window.fbq("track", evento, params || {}, eventId ? { eventID: eventId } : undefined);
  } catch (_) { /* nunca frenar la UI por tracking */ }
}

/**
 * Manda el lead al backend. `sendBeacon` primero porque el caso del botón de WhatsApp navega a
 * otra app INMEDIATAMENTE: un `fetch` normal se cancela al irse la página y el lead se pierde
 * —justo el canal que hoy es invisible—. Beacon sobrevive a la navegación.
 *
 * Devuelve el resultado para que el llamador pueda reintentar, pero NUNCA rechaza: un problema de
 * red del tracking no puede romper el formulario.
 */
export function enviarLead(payload) {
  const cuerpo = JSON.stringify({ ...payload, visitId: visitId(), ref: refCode() });
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([cuerpo], { type: "application/json" });
      if (navigator.sendBeacon("/lead-api/lead", blob)) return Promise.resolve({ ok: true, beacon: true });
    }
  } catch (_) { /* cae al fetch */ }
  return fetch("/lead-api/lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: cuerpo,
    keepalive: true,
  })
    .then((r) => ({ ok: r.ok, status: r.status }))
    .catch(() => ({ ok: false }));
}
